import { useState, useEffect, useReducer } from 'react'
import { useParticipants, useRoomContext } from '@livekit/components-react'
import { RoomEvent, Track } from 'livekit-client'
import { apiFetch } from '../lib/apiFetch'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-participants')

interface ParticipantListProps {
  roomId: string
  isAdmin: boolean
  onClose: () => void
}

export function ParticipantList({ roomId, isAdmin, onClose }: ParticipantListProps) {
  const participants = useParticipants()
  const room = useRoomContext()
  const [waitingList, setWaitingList] = useState<any[]>([])
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  // Force re-render on track state changes so mic/cam icons stay in sync
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    const update = () => forceUpdate()
    room.on(RoomEvent.TrackMuted, update)
    room.on(RoomEvent.TrackUnmuted, update)
    room.on(RoomEvent.TrackPublished, update)
    room.on(RoomEvent.TrackUnpublished, update)
    room.on(RoomEvent.TrackSubscribed, update)
    room.on(RoomEvent.TrackUnsubscribed, update)
    room.on(RoomEvent.ParticipantMetadataChanged, update)
    return () => {
      room.off(RoomEvent.TrackMuted, update)
      room.off(RoomEvent.TrackUnmuted, update)
      room.off(RoomEvent.TrackPublished, update)
      room.off(RoomEvent.TrackUnpublished, update)
      room.off(RoomEvent.TrackSubscribed, update)
      room.off(RoomEvent.TrackUnsubscribed, update)
      room.off(RoomEvent.ParticipantMetadataChanged, update)
    }
  }, [room])

  const loadWaiting = async () => {
    if (!isAdmin) return
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/waiting`)
      if (res.ok) {
        const data = await res.json()
        setWaitingList(data.waiting || [])
      }
    } catch {}
  }

  // Poll waiting list every 5s if admin
  useEffect(() => {
    if (!isAdmin) return
    loadWaiting()
    const id = setInterval(loadWaiting, 5000)
    return () => clearInterval(id)
  }, [isAdmin, roomId])

  const admitParticipant = async (pid: string) => {
    setLoading(l => ({ ...l, [pid]: true }))
    try {
      await apiFetch(`/api/visio/rooms/${roomId}/admit/${pid}`, { method: 'POST' })
      slog.action('admit', { roomId, pid })
      await loadWaiting()
    } catch {} finally {
      setLoading(l => ({ ...l, [pid]: false }))
    }
  }

  const rejectParticipant = async (pid: string) => {
    setLoading(l => ({ ...l, [pid]: true }))
    try {
      await apiFetch(`/api/visio/rooms/${roomId}/reject/${pid}`, { method: 'POST' })
      slog.action('reject', { roomId, pid })
      await loadWaiting()
    } catch {} finally {
      setLoading(l => ({ ...l, [pid]: false }))
    }
  }

  const muteParticipant = async (identity: string, trackType: 'audio' | 'video' = 'audio') => {
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/participants`)
      if (res.ok) {
        const data = await res.json()
        const p = data.participants?.find((p: any) => p.user_id === identity)
        if (p) {
          await apiFetch(`/api/visio/rooms/${roomId}/mute/${p.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ track_type: trackType }),
          })
          slog.action('mute-remote', { roomId, identity, trackType })
        }
      }
    } catch (err) {
      console.warn('[ParticipantList] muteParticipant failed:', err)
    }
  }

  const muteAll = async () => {
    try {
      await apiFetch(`/api/visio/rooms/${roomId}/mute-all`, { method: 'POST' })
      slog.action('mute-all', { roomId })
    } catch {}
  }

  const promoteParticipant = async (identity: string) => {
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/participants`)
      if (res.ok) {
        const data = await res.json()
        const p = data.participants?.find((p: any) => p.user_id === identity)
        if (p) {
          await apiFetch(`/api/visio/rooms/${roomId}/promote/${p.id}`, { method: 'POST' })
          slog.action('promote', { roomId, identity })
        }
      }
    } catch {}
  }

  const getMeta = (p: any) => {
    try { return JSON.parse(p.metadata || '{}') } catch { return {} }
  }

  const roleOrder: Record<string, number> = { admin: 0, organizer: 1, participant: 2, guest: 3 }
  const sorted = [...participants].sort((a, b) => {
    const ma = getMeta(a)
    const mb = getMeta(b)
    return (roleOrder[ma.role] ?? 9) - (roleOrder[mb.role] ?? 9)
  })

  return (
    <div className="absolute right-0 top-0 bottom-12 w-72 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-sm font-medium text-zinc-200">Participants ({participants.length})</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-white text-lg leading-none">&times;</button>
      </div>

      {/* Admin controls */}
      {isAdmin && participants.length > 1 && (
        <div className="px-3 py-2 border-b border-zinc-700 flex gap-1">
          <button onClick={muteAll} className="flex-1 px-2 py-1 text-[11px] bg-red-600 hover:bg-red-700 text-white rounded">
            Couper tous les micros
          </button>
        </div>
      )}

      {/* Waiting room */}
      {isAdmin && waitingList.length > 0 && (
        <div className="px-3 py-2 border-b border-zinc-700">
          <div className="text-[10px] text-yellow-400 uppercase font-semibold mb-1">En attente ({waitingList.length})</div>
          {waitingList.map(w => (
            <div key={w.id} className="flex items-center justify-between py-1">
              <span className="text-xs text-zinc-300">{w.display_name || 'Anonyme'}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => admitParticipant(w.id)}
                  disabled={loading[w.id]}
                  className="px-2 py-0.5 text-[10px] bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
                >
                  Admettre
                </button>
                <button
                  onClick={() => rejectParticipant(w.id)}
                  disabled={loading[w.id]}
                  className="px-2 py-0.5 text-[10px] bg-zinc-600 hover:bg-zinc-500 text-zinc-200 rounded disabled:opacity-50"
                >
                  Refuser
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Participant list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {sorted.map(p => {
          const meta = getMeta(p)
          const isMicOn = p.isMicrophoneEnabled
          const isCamOn = p.isCameraEnabled
          const hasMicTrack = !!p.getTrackPublication(Track.Source.Microphone)
          const handRaised = meta.handRaised
          const role = meta.role || 'participant'
          const isLocal = p.isLocal

          return (
            <div key={p.identity} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-zinc-800 group">
              {/* Avatar */}
              <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-[11px] text-zinc-300 font-medium shrink-0">
                {(p.name || p.identity || '?')[0].toUpperCase()}
              </div>

              {/* Name + role */}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-200 truncate">
                  {p.name || p.identity}
                  {isLocal && <span className="text-zinc-500 ml-1">(vous)</span>}
                </div>
                {role !== 'participant' && (
                  <span className={`text-[9px] px-1 py-0.5 rounded ${
                    role === 'admin' ? 'bg-blue-600/30 text-blue-300' :
                    role === 'organizer' ? 'bg-purple-600/30 text-purple-300' :
                    role === 'guest' ? 'bg-zinc-600/30 text-zinc-400' : ''
                  }`}>
                    {role === 'admin' ? 'Admin' : role === 'organizer' ? 'Org' : role === 'guest' ? 'Guest' : role}
                  </span>
                )}
              </div>

              {/* Hand raised */}
              {handRaised && (
                <span className="text-yellow-400 text-sm" title="Main levee">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M7 11.5V14a1 1 0 01-2 0V7.5a1.5 1.5 0 013 0V11h.5a1 1 0 011 1v1.5a1 1 0 01-2 0V12h-.5zM11 11V5.5a1.5 1.5 0 013 0V11h1V4.5a1.5 1.5 0 013 0V11h1V6.5a1.5 1.5 0 013 0V15a7 7 0 01-14 0v-4z"/>
                  </svg>
                </span>
              )}

              {/* Mic: green=on, red crossed=muted, grey=not available */}
              {isLocal ? (
                <button onClick={() => room.localParticipant.setMicrophoneEnabled(!isMicOn)} className={`${isMicOn ? 'text-emerald-400 hover:text-red-400' : 'text-red-400 hover:text-emerald-400'} transition-colors`} title={isMicOn ? 'Couper votre micro' : 'Activer votre micro'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    {isMicOn ? (
                      <><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" /></>
                    ) : (
                      <><path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" /><path d="M17 16.95A7 7 0 015 12m14 0a7 7 0 01-.11 1.23" /></>
                    )}
                  </svg>
                </button>
              ) : isMicOn ? (
                isAdmin ? (
                  <button onClick={() => muteParticipant(p.identity, 'audio')} className="text-emerald-400 hover:text-red-400 transition-colors" title="Couper le micro">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" />
                    </svg>
                  </button>
                ) : (
                  <span className="text-emerald-400" title="Micro actif">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" />
                    </svg>
                  </span>
                )
              ) : hasMicTrack ? (
                <span className="text-red-400" title="Micro coupe">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" /><path d="M17 16.95A7 7 0 015 12m14 0a7 7 0 01-.11 1.23" />
                  </svg>
                </span>
              ) : (
                <span className="text-zinc-500" title="Micro non disponible">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" />
                  </svg>
                </span>
              )}

              {/* Cam: green=on, grey=not available */}
              {isLocal ? (
                <button onClick={async () => { try { await room.localParticipant.setCameraEnabled(!isCamOn) } catch (err) { console.warn('[ParticipantList] setCameraEnabled failed:', err) } }} className={`${isCamOn ? 'text-emerald-400 hover:text-red-400' : 'text-zinc-500 hover:text-emerald-400'} transition-colors`} title={isCamOn ? 'Couper votre camera' : 'Activer votre camera'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    {isCamOn ? (
                      <><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></>
                    ) : (
                      <><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></>
                    )}
                  </svg>
                </button>
              ) : isCamOn ? (
                isAdmin ? (
                  <button onClick={() => muteParticipant(p.identity, 'video')} className="text-emerald-400 hover:text-red-400 transition-colors" title="Couper la camera">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                      <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
                    </svg>
                  </button>
                ) : (
                  <span className="text-emerald-400" title="Camera active">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                      <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
                    </svg>
                  </span>
                )
              ) : (
                <span className="text-zinc-500" title="Camera non disponible">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
                  </svg>
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
